import {Box, Button, Grid, Tab, Typography} from '@mui/material';
import {TabContext, TabList, TabPanel} from '@mui/lab';
import {useRef, useState, useEffect, useContext} from 'react';
import ConfirmationDialog2 from '../../../components/dialogbox/confirmationDialog2';
import APIServiceFormContext from '../../../context/APIServiceFormContext';
import {BodyLocationComponent} from './individualBodyLocation';

/**
 * BodyLocationsComponent
 * Wrapper of the individualBodyLocation in a way that handles all the body_location questions from dermadetect being user friendly. Works with the dermadetectForm system
 *
 * @returns {React.Component}
 */
export const BodyLocationsComponent = () => {
  const {setValidationFunc, savedValue, additionalSavedData, setSendDataArgs} = useContext(APIServiceFormContext);
  const [totalLocations, setTotalLocations] = useState(1);
  const [instantError, setInstantError] = useState(undefined);

  const [location1Data, setLocation1Data] = useState({});
  const [tempLocation1Data, setTempLocation1Data] = useState({});
  const [location1AdditionalData, setLocation1AdditionalData] = useState({});
  const [tempLocation1AdditionalData, setTempLocation1AdditionalData] = useState({});

  const [location2Data, setLocation2Data] = useState(undefined);
  const [tempLocation2Data, setTempLocation2Data] = useState(undefined);
  const [location2AdditionalData, setLocation2AdditionalData] = useState(undefined);
  const [tempLocation2AdditionalData, setTempLocation2AdditionalData] = useState({});
  const [openDeleteModalLocation2, setOpenDeleteModalLocation2] = useState(false);
  const [location2ID, setLocation2ID] = useState(1);

  const [location3Data, setLocation3Data] = useState(undefined);
  const [tempLocation3Data, setTempLocation3Data] = useState(undefined);
  const [location3AdditionalData, setLocation3AdditionalData] = useState(undefined);
  const [tempLocation3AdditionalData, setTempLocation3AdditionalData] = useState({});
  const [openDeleteModalLocation3, setOpenDeleteModalLocation3] = useState(false);
  const [location3ID, setLocation3ID] = useState(2);

  const [currentLocation, setCurrentLocation] = useState(0);
  const locationDataStateArray = [
    {
      id: 0,
      value: location1Data,
      setSavedValue: setLocation1Data,
      setValue: setTempLocation1Data,
      tempValue: tempLocation1Data,
      additionalData: location1AdditionalData,
      setAdditionalData: setTempLocation1AdditionalData,
      setSavedAdditionalData: setLocation1AdditionalData,
      tempAdditionalData: tempLocation1AdditionalData,
    },
    {
      id: location2ID,
      setID: setLocation2ID,
      value: location2Data,
      setSavedValue: setLocation2Data,
      setValue: setTempLocation2Data,
      tempValue: tempLocation2Data,
      additionalData: location2AdditionalData,
      setAdditionalData: setTempLocation2AdditionalData,
      setSavedAdditionalData: setLocation2AdditionalData,
      tempAdditionalData: tempLocation2AdditionalData,
      openDeleteModal: openDeleteModalLocation2,
      setOpenDeleteModal: setOpenDeleteModalLocation2,
    },
    {
      id: location3ID,
      setID: setLocation3ID,
      value: location3Data,
      setSavedValue: setLocation3Data,
      setValue: setTempLocation3Data,
      tempValue: tempLocation3Data,
      additionalData: location3AdditionalData,
      setAdditionalData: setTempLocation3AdditionalData,
      setSavedAdditionalData: setLocation3AdditionalData,
      tempAdditionalData: tempLocation3AdditionalData,
      openDeleteModal: openDeleteModalLocation3,
      setOpenDeleteModal: setOpenDeleteModalLocation3,
    },
  ];

  const componentValidationWrapper = () => {
    function componentValidation() {
      const allLocations = locationDataStateArray
        .filter(l => l.value !== undefined || l.tempValue !== undefined)
        .sort((l1, l2) => l1.id - l2.id);
      for (let location of allLocations) {
        const dataValue =
          location.tempValue === undefined || location.tempValue.primary === ''
            ? location.value
            : {...location.tempValue};

        if (Object.keys(dataValue).length === 0 || dataValue.primary === '') {
          setCurrentLocation(location.id);
          return `Please select a primary location in location ${location.id + 1}`;
        }

        if (dataValue.secondary.length === 0) {
          setCurrentLocation(location.id);
          return `Please select one or more secondary locations in location ${location.id + 1}`;
        }

        if (dataValue.image_urls.length < 2) {
          setCurrentLocation(location.id);
          return `Please upload at least 2 photos in location ${location.id + 1}.`;
        }
      }
      return '';
    }
    componentValidation.instantError = instantError;
    return componentValidation;
  };

  const [componentValidation, setComponentValidation] = useState(() => componentValidationWrapper);
  const timeoutRef = useRef(null);
  useEffect(() => {
    if (savedValue === undefined || additionalSavedData === undefined) return;
    // the saved value will be an array of objects, each object contains data about a location
    for (let i = 0; i < savedValue.length; i++) {
      locationDataStateArray[i].setSavedValue(savedValue[i]);
      locationDataStateArray[i].setValue(savedValue[i]);
      locationDataStateArray[i].setSavedAdditionalData(additionalSavedData[i]);
      locationDataStateArray[i].setAdditionalData(additionalSavedData[i]);
    }
    setTotalLocations(savedValue.length);
  }, [savedValue, additionalSavedData]);

  useEffect(() => {
    setComponentValidation(() => componentValidationWrapper);
  }, [instantError]);
  useEffect(() => {
    setValidationFunc(componentValidation);
  }, [componentValidation]);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (instantError === undefined) return;
    timeoutRef.current = setTimeout(() => {
      setInstantError(undefined);
    }, 4000);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [instantError]);

  useEffect(() => {
    setComponentValidation(() => componentValidationWrapper);
    // keys and locationsToSend are dependent on each other for the data to be sent correctly
    //getting all the attributes for each location in an array
    let locationsDataObject = locationDataStateArray
      .map(location => {
        return {
          value: location.tempValue,
          additionalData: location.tempAdditionalData,
        };
      })
      .filter(location => location.value !== undefined);
    let locationsToSend = locationsDataObject
      .map(location => {
        return [location.value.secondary, location.value.primary, location.value.image_urls];
      })
      .flat();
    let additionalDataToSend = locationsDataObject.map(location => ({
      previousUploadedFilesLength: location.additionalData.previousUploadedFilesLength,
      uploadedFiles: location.additionalData.uploadedFiles,
    }));

    let keys = [];
    for (let i = 0; i < totalLocations; i++) {
      const currentLocation = `location${i + 1}`;
      // this is the order in which the attributes of each location are
      keys.push(`${currentLocation}.secondary`, `${currentLocation}.primary`, `${currentLocation}.image_urls`);
    }

    //based on how many locations there were, adding the corresponding 'add_location_n' attributes
    let numberOfAddLocations = totalLocations === locationDataStateArray.length ? totalLocations - 1 : totalLocations;

    for (let i = 0; i < numberOfAddLocations; i++) {
      locationsToSend.push(i + 1 < totalLocations);
      keys.push(`add_location_${i + 2}`);
    }

    setSendDataArgs({
      data: locationsToSend,
      keyHandling: {
        staticParts: keys,
      },
      additional: additionalDataToSend,
    });
  }, [savedValue, tempLocation1Data, tempLocation2Data, tempLocation3Data]);

  return (
    <TabContext value={currentLocation.toString()}>
      <Box
        sx={{
          width: '100%',
          zIndex: 200,
          background: 'white',
          pb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            my: {xs: 1, sm: 2},
            mx: {xs: 2, sm: 0},
          }}
        >
          Location of the Skin Problem
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Select up to 3 primary locations.
        </Typography>
        <TabList
          onChange={(_, val) => {
            if (val !== 'add') {
              setCurrentLocation(val);
              return;
            }
            const currentList = locationDataStateArray
              .filter(l => l.value !== undefined || l.tempValue !== undefined)
              .sort((l1, l2) => l1.id - l2.id);
            const presentLocation = currentList[currentList.length - 1];

            const dataValue =
              presentLocation.tempValue === undefined || presentLocation.tempValue.primary === ''
                ? presentLocation.value
                : {...presentLocation.tempValue};

            if (
              Object.keys(dataValue).length === 0 ||
              dataValue === undefined ||
              dataValue === null ||
              dataValue.primary === '' ||
              dataValue.secondary.length <= 0 ||
              dataValue.image_urls.length < 2
            ) {
              setInstantError('Please complete the last location first in order to add a new one.');
              setCurrentLocation(presentLocation.id);
              return;
            }
            let newLocation = locationDataStateArray.find(data => data.id === presentLocation.id + 1);
            let newID = newLocation.id;

            setCurrentLocation(newID);
            setTotalLocations(val => val + 1);
            newLocation.setValue({});
            newLocation.setAdditionalData({});
            newLocation.setSavedValue({});
            newLocation.setOpenDeleteModal(false);
            newLocation.setSavedAdditionalData({});
          }}
          sx={{
            '& .MuiTabs-flexContainer': {
              justifyContent: {
                xs: 'center',
                sm: 'flex-start',
              },
            },
            '& .MuiButtonBase-root': {
              p: {
                xs: '8px',
                sm: '12px 16px',
              },
              fontSize: {
                xs: 12,
                sm: '0.875rem',
              },
            },
          }}
        >
          {locationDataStateArray
            .filter(l => l.value !== undefined || l.tempValue !== undefined)
            .sort((l1, l2) => l1.id - l2.id)
            .map((location, idx) => {
              return <Tab key={idx} label={`Location ${idx + 1}`} value={location.id.toString()} />;
            })}
          {totalLocations <= 2 && (
            <Tab
              label="+ Add location"
              value="add"
              sx={{
                fontWeight: 'bold',
                '&:hover': {
                  color: '#35C6C9',
                },
              }}
            />
          )}
        </TabList>
      </Box>
      <Grid
        container
        sx={{
          flexDirection: 'column',
          '& .MuiTabPanel-root': {
            p: 0,
          },
        }}
      >
        {locationDataStateArray
          .filter(l => l.value !== undefined)
          .sort((l1, l2) => l1.id - l2.id)
          .map((locationData, idx) => {
            const dataValue =
              locationData.tempValue === undefined || locationData.tempValue.primary === ''
                ? locationData.value
                : {...locationData.tempValue};
            const additionalDataValue =
              locationData.tempAdditionalData.previousUploadedFilesLength === 0 &&
              locationData.tempAdditionalData.uploadedFiles.length === 0 &&
              locationData.tempValue.image_urls.length !== 0
                ? locationData.additionalData
                : {...locationData.tempAdditionalData};

            return (
              <TabPanel value={locationData.id.toString()} key={idx}>
                <Box
                  sx={{
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '0 auto',
                    backgroundColor: '#FBFBFB',
                    borderRadius: 5,
                    py: 2,
                    px: 4,
                  }}
                >
                  {idx !== 0 && (
                    <>
                      <Button
                        color="error"
                        sx={{
                          ml: 'auto',
                          mb: 2,
                        }}
                        onClick={() => {
                          locationData.setOpenDeleteModal(true);
                        }}
                      >
                        Delete location
                      </Button>
                      <ConfirmationDialog2
                        open={locationData.openDeleteModal}
                        title={`Deleting location data`}
                        subtitle={`Are you sure you want to delete location ${locationData.id + 1}?`}
                        onCloseText="No"
                        onConfirmText="Yes"
                        onClose={() => {
                          locationData.setOpenDeleteModal(false);
                        }}
                        onConfirm={() => {
                          locationData.setOpenDeleteModal(false);
                          if (locationData.id === 1 && totalLocations === 3) {
                            const locationDataWithID2 = locationDataStateArray.find(l => l.id === 2);
                            locationDataWithID2.setID(1);
                            locationData.setID(2);
                          }

                          locationData.setValue(undefined);
                          locationData.setSavedValue(undefined);
                          locationData.setAdditionalData(undefined);
                          locationData.setSavedAdditionalData(undefined);
                          setTotalLocations(total => total - 1);
                          setCurrentLocation(0);
                        }}
                      />
                    </>
                  )}
                  <BodyLocationComponent
                    data={dataValue}
                    setData={locationData.setValue}
                    additionalData={additionalDataValue}
                    setAdditionalData={locationData.setAdditionalData}
                  />
                </Box>
              </TabPanel>
            );
          }, totalLocations)}
      </Grid>
    </TabContext>
  );
};
